import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <div className="mb-4 flex items-center gap-3 text-red-600">
              <AlertCircle size={32} />
              <h1 className="text-2xl font-bold text-balance">משהו השתבש</h1>
            </div>

            <p className="mb-4 text-gray-600">אירעה שגיאה בלתי צפויה. אנא נסה לרענן את הדף.</p>

            {this.state.error && (
              <pre className="mb-4 overflow-x-auto rounded bg-gray-100 p-3 text-sm text-gray-800">
                {this.state.error.message}
              </pre>
            )}

            <div className="flex gap-2">
              <button
                onClick={this.handleReset}
                className="flex-1 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-[background-color,transform] hover:bg-blue-700 active:scale-[0.96]"
              >
                חזור לדף הבית
              </button>
              <button
                onClick={() => window.location.reload()}
                className="flex-1 rounded-lg bg-gray-200 px-4 py-2 font-medium text-gray-800 transition-[background-color,transform] hover:bg-gray-300 active:scale-[0.96]"
              >
                רענן דף
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
